import type { Router } from 'vue-router';
import {
  AlertIcon,
  ClipboardNoteIcon,
  ClockIcon,
  CollectionsAddIcon,
  HighlightIcon,
  TableCalculatorIcon,
} from '@blpde/nx-icons';
import type { NxCommand } from '@blpde/nx-ui';
import { erpListRoute, loadErpDefinitions } from '@erp/connect/shell';

export interface TasksCommandContext {
  router: Router;
  t: (key: string, named?: Record<string, unknown>) => string;
  /** Connect shell label, e.g. translated app name */
  groupLabel?: string;
}

/** Tabs that open the list route of a resource — its name is the served route table's. */
const TAB_COMMAND_RESOURCES: Record<string, string> = {
  table: 'tasks',
  projects: 'projects',
};

/** Tabs that are plain ERP routes of their own. */
const TAB_COMMAND_ROUTES: Record<string, string> = {
  today: 'TasksMyDay',
  'time-tracking': 'TasksTimeTracking',
  overruns: 'TasksOverruns',
};

function tabRouteName(tab: string): string {
  const resource = Object.hasOwn(TAB_COMMAND_RESOURCES, tab) ? TAB_COMMAND_RESOURCES[tab] : 'tasks';
  return Object.hasOwn(TAB_COMMAND_ROUTES, tab)
    ? TAB_COMMAND_ROUTES[tab]
    : erpListRoute(resource).name;
}

/** The route names come from the ERP's definitions, so the jump waits for them. */
function goTab(ctx: TasksCommandContext, tab: string) {
  return () => {
    void loadErpDefinitions().then(() => ctx.router.push({ name: tabRouteName(tab) }));
  };
}

/** Static navigation commands — live ERP tasks tabs (kit icons). */
export function buildTasksConnectCommands(ctx: TasksCommandContext): NxCommand[] {
  const group = ctx.groupLabel ?? ctx.t('forge.brand');
  const prefix = (label: string) => `${group}: ${label}`;

  return [
    {
      id: 'erp.tasks.nav.today',
      label: prefix(ctx.t('nav.today')),
      group,
      icon: HighlightIcon,
      keywords: ['mein tag', 'today', 'myday'],
      run: goTab(ctx, 'today'),
    },
    {
      id: 'erp.tasks.nav.table',
      label: prefix(ctx.t('nav.tasksTable')),
      group,
      icon: TableCalculatorIcon,
      keywords: ['tasks', 'tabelle', 'alle aufgaben', 'inbox'],
      run: goTab(ctx, 'table'),
    },
    {
      id: 'erp.tasks.nav.projects',
      label: prefix(ctx.t('nav.projects')),
      group,
      icon: CollectionsAddIcon,
      keywords: ['projects', 'projekte'],
      run: goTab(ctx, 'projects'),
    },
    {
      id: 'erp.tasks.nav.timeTracking',
      label: prefix(ctx.t('nav.timeTracking')),
      group,
      icon: ClockIcon,
      keywords: ['time', 'tracking', 'timer', 'zeiterfassung'],
      run: goTab(ctx, 'time-tracking'),
    },
    {
      id: 'erp.tasks.nav.overruns',
      label: prefix(ctx.t('nav.overruns')),
      group,
      icon: AlertIcon,
      keywords: ['frühwarnung', 'overrun', 'warn'],
      run: goTab(ctx, 'overruns'),
    },
    {
      id: 'erp.tasks.nav.brand',
      label: prefix(ctx.t('forge.brand')),
      group,
      icon: ClipboardNoteIcon,
      keywords: ['tasks', 'aufgaben', 'erp'],
      run: goTab(ctx, 'table'),
    },
    {
      id: 'erp.tasks.newTask',
      label: prefix(ctx.t('tasks.actions.newTask')),
      group,
      icon: ClipboardNoteIcon,
      keywords: ['neu', 'create', 'aufgabe'],
      run: goTab(ctx, 'table'),
    },
  ];
}
